/**
 * SSR-safe Browser Router Factory
 *
 * Wraps React Router's createBrowserRouter with the mandatory pre-resolution
 * step that prevents hydration mismatches when using lazy() routes with SSR.
 *
 * The protocol:
 *   1. matchRoutes() to find which lazy routes match the current URL
 *   2. Resolve those lazy modules (download the code-split chunks)
 *   3. Patch the route objects in-place (Component resolved, lazy removed)
 *   4. THEN create the browser router
 *
 * Without this, React Router renders null while lazy chunks load, mismatching
 * the server-rendered HTML and causing React to duplicate the page.
 *
 * Direct use of createBrowserRouter is banned in this app via ESLint.
 * All client routing must go through this wrapper.
 */
import {
  // eslint-disable-next-line no-restricted-syntax -- This is the one safe wrapper; all other code must use createHydratedRouter.
  createBrowserRouter,
  matchRoutes,
  type HydrationState,
  type RouteObject,
} from 'react-router';

type DataStrategyFunction = NonNullable<Parameters<typeof createBrowserRouter>[1]>['dataStrategy'];

interface HydratedRouterOptions {
  hydrationData?: HydrationState;
  dataStrategy?: DataStrategyFunction;
}

export async function createHydratedRouter(
  routes: RouteObject[],
  options: HydratedRouterOptions = {}
) {
  const matched = matchRoutes(routes, window.location.pathname);
  if (matched) {
    await Promise.all(
      matched
        .filter((m) => typeof m.route.lazy === 'function')
        .map(async (m) => {
          const lazyFn = m.route.lazy as () => Promise<Partial<RouteObject>>;
          const mod = await lazyFn();
          Object.assign(m.route, mod);
          delete m.route.lazy;
        })
    );
  }

  return createBrowserRouter(routes, {
    hydrationData: options.hydrationData,
    dataStrategy: options.dataStrategy,
  });
}
